import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    CalendarIcon,
    Loader2,
    X,
    Check,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import type { Task, TaskStatus, TaskPriority, ProjectMember, CustomField } from '@/lib/schemas';
import { statusConfig, priorityConfig, getStatusLabel, getPriorityLabel } from './task-table';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

// ============================================
// FORM SCHEMA
// ============================================

const taskFormSchema = z.object({
    title: z.string().min(1, 'Task title is required').max(500),
    description: z.string().max(10000).default(''),
    status: z.enum(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED']),
    priority: z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH', 'URGENT']),
    dueDate: z.string().default(''),
    assigneeIds: z.array(z.string()),
});

type TaskFormData = z.infer<typeof taskFormSchema>;

// ============================================
// PROPS
// ============================================

interface TaskDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    task?: Task | null;
    projectMembers: ProjectMember[];
    projectOwnerId: string;
    projectOwner: { id: string; name: string; email: string; avatarUrl: string | null };
    onSubmit: (data: TaskFormData & { customFieldValues?: Record<string, unknown> }) => Promise<void>;
    isLoading?: boolean;
    customFields?: CustomField[];
    /**
     * t18: when creating a new task (no `task` prop), prefill the due
     * date field with this YYYY-MM-DD value. Used by the Calendar view's
     * right-click "Create task at <date>" action so the dialog opens with
     * the right day already selected. Ignored when editing.
     */
    initialDueDate?: string | null;
}

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function formatDateForInput(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0] ?? '';
    } catch {
        return '';
    }
}

// ============================================
// COMPONENT
// ============================================

export function TaskDialog({
    open,
    onOpenChange,
    task,
    projectMembers,
    projectOwnerId,
    projectOwner,
    onSubmit,
    isLoading,
    customFields = [],
    initialDueDate = null,
}: TaskDialogProps) {
    const { t } = useI18n();
    const isEditing = !!task;
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});

    // Combine owner with members for assignee selection
    const allMembers = useMemo(() => {
        const members = [
            {
                id: 'owner',
                userId: projectOwnerId,
                role: 'OWNER' as const,
                joinedAt: '',
                user: projectOwner,
            },
            ...projectMembers.filter((m) => m.userId !== projectOwnerId),
        ];
        return members;
    }, [projectMembers, projectOwnerId, projectOwner]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<TaskFormData>({
        resolver: zodResolver(taskFormSchema),
        defaultValues: {
            title: '',
            description: '',
            status: 'TODO',
            priority: 'NONE',
            dueDate: '',
            assigneeIds: [],
        },
    });

    const selectedAssignees = watch('assigneeIds');
    const selectedStatus = watch('status');
    const selectedPriority = watch('priority');

    // Reset form when dialog opens/closes or task changes
    useEffect(() => {
        if (open) {
            if (task) {
                reset({
                    title: task.title,
                    description: task.description || '',
                    status: task.status,
                    priority: task.priority,
                    dueDate: formatDateForInput(task.dueDate),
                    assigneeIds: task.assignees.map((a) => a.userId),
                });
                setCustomFieldValues(task.customFieldValues || {});
            } else {
                reset({
                    title: '',
                    description: '',
                    status: 'TODO',
                    priority: 'NONE',
                    // t18: prefill from initialDueDate when the parent
                    // (Calendar view's right-click menu) passed a day.
                    // Accept YYYY-MM-DD or full ISO; formatDateForInput
                    // slices to YYYY-MM-DD either way.
                    dueDate: initialDueDate ? formatDateForInput(initialDueDate) : '',
                    assigneeIds: [],
                });
                setCustomFieldValues({});
            }
        }
    }, [open, task, reset, initialDueDate]);

    const handleFormSubmit = async (data: TaskFormData) => {
        // Convert dueDate from date input format (YYYY-MM-DD) to ISO datetime string
        let convertedDueDate: string | null = null;
        if (data.dueDate) {
            // Create a date object and convert to ISO string
            const dateObj = new Date(data.dueDate + 'T00:00:00.000Z');
            convertedDueDate = dateObj.toISOString();
        }

        const submitData = {
            ...data,
            dueDate: convertedDueDate,
            customFieldValues: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
        };
        await onSubmit(submitData as any);
    };

    const toggleAssignee = (userId: string) => {
        const current = selectedAssignees || [];
        if (current.includes(userId)) {
            setValue(
                'assigneeIds',
                current.filter((id) => id !== userId)
            );
        } else {
            setValue('assigneeIds', [...current, userId]);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? t('taskDialog.editTitle') : t('taskDialog.createTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('taskDialog.editDescription')
                            : t('taskDialog.createDescription')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                    {/* Title */}
                    <div className="space-y-2">
                        <Label htmlFor="title">
                            {t('taskDialog.title')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="title"
                            placeholder={t('taskDialog.titlePlaceholder')}
                            {...register('title')}
                            className={errors.title ? 'border-destructive' : ''}
                        />
                        {errors.title && (
                            <p className="text-sm text-destructive">{errors.title.message}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description">{t('taskDialog.description')}</Label>
                        <Textarea
                            id="description"
                            placeholder={t('taskDialog.descriptionPlaceholder')}
                            rows={3}
                            {...register('description')}
                            className={errors.description ? 'border-destructive' : ''}
                        />
                        {errors.description && (
                            <p className="text-sm text-destructive">
                                {errors.description.message}
                            </p>
                        )}
                    </div>

                    {/* Status and Priority row */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Status */}
                        <div className="space-y-2">
                            <Label>{t('tasks.status')}</Label>
                            <Select
                                value={selectedStatus}
                                onValueChange={(value) => setValue('status', value as TaskStatus)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('taskDialog.selectStatus')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(statusConfig).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={config.variant} className="text-xs">
                                                    {getStatusLabel(key as TaskStatus, t)}
                                                </Badge>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Priority */}
                        <div className="space-y-2">
                            <Label>{t('tasks.priority')}</Label>
                            <Select
                                value={selectedPriority}
                                onValueChange={(value) => setValue('priority', value as TaskPriority)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('taskDialog.selectPriority')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(priorityConfig).map(([key, config]) => (
                                        <SelectItem key={key} value={key}>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={config.variant} className="text-xs">
                                                    {getPriorityLabel(key as TaskPriority, t)}
                                                </Badge>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Due Date */}
                    <div className="space-y-2">
                        <Label htmlFor="dueDate">{t('tasks.dueDate')}</Label>
                        <div className="relative">
                            <Input
                                id="dueDate"
                                type="date"
                                {...register('dueDate')}
                                className="pl-10"
                            />
                            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                        </div>
                    </div>

                    {/* Assignees */}
                    <div className="space-y-2">
                        <Label>{t('tasks.assignees')}</Label>
                        <div className="border border-border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                            {allMembers.map((member) => (
                                <label
                                    key={member.userId}
                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-hover cursor-pointer"
                                >
                                    <Checkbox
                                        checked={selectedAssignees?.includes(member.userId)}
                                        onCheckedChange={() => toggleAssignee(member.userId)}
                                    />
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                        <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                                            {getInitials(member.user.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {member.user.name}
                                        </p>
                                        <p className="text-xs text-foreground-muted truncate">
                                            {member.user.email}
                                        </p>
                                    </div>
                                    {member.role === 'OWNER' && (
                                        <Badge variant="outline" className="text-xs">
                                            {t('taskDialog.owner')}
                                        </Badge>
                                    )}
                                </label>
                            ))}
                            {allMembers.length === 0 && (
                                <p className="text-sm text-foreground-muted text-center py-4">
                                    {t('taskDialog.noTeamMembers')}
                                </p>
                            )}
                        </div>
                        {/* Selected assignees display */}
                        {selectedAssignees && selectedAssignees.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {selectedAssignees.map((userId) => {
                                    const member = allMembers.find((m) => m.userId === userId);
                                    if (!member) return null;
                                    return (
                                        <Badge
                                            key={userId}
                                            variant="secondary"
                                            className="pl-1 pr-2 py-1"
                                        >
                                            <Avatar className="h-5 w-5 mr-1">
                                                <AvatarImage src={member.user.avatarUrl || undefined} />
                                                <AvatarFallback className="text-[10px]">
                                                    {getInitials(member.user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            {member.user.name}
                                            <button
                                                type="button"
                                                onClick={() => toggleAssignee(userId)}
                                                className="ml-1 hover:text-destructive"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Custom Fields */}
                    {customFields.length > 0 && (
                        <div className="space-y-4">
                            <div className="border-t border-border pt-4">
                                <Label className="text-base font-medium">{t('taskDialog.customFields')}</Label>
                            </div>
                            {customFields.map((field) => (
                                <div key={field.id} className="space-y-2">
                                    <Label htmlFor={`custom-${field.id}`}>
                                        {field.name}
                                        {field.isRequired && <span className="text-destructive ml-1">*</span>}
                                    </Label>

                                    {/* TEXT field */}
                                    {field.type === 'TEXT' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onChange={(e) => setCustomFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: e.target.value
                                            }))}
                                            placeholder={`Enter ${field.name.toLowerCase()}...`}
                                        />
                                    )}

                                    {/* NUMBER field */}
                                    {field.type === 'NUMBER' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            type="number"
                                            value={(customFieldValues[field.id] as number) ?? ''}
                                            onChange={(e) => setCustomFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: e.target.value ? Number(e.target.value) : null
                                            }))}
                                            placeholder={`Enter ${field.name.toLowerCase()}...`}
                                        />
                                    )}

                                    {/* DATE field */}
                                    {field.type === 'DATE' && (
                                        <div className="relative">
                                            <Input
                                                id={`custom-${field.id}`}
                                                type="date"
                                                value={(customFieldValues[field.id] as string) || ''}
                                                onChange={(e) => setCustomFieldValues(prev => ({
                                                    ...prev,
                                                    [field.id]: e.target.value || null
                                                }))}
                                                className="pl-10"
                                            />
                                            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                                        </div>
                                    )}

                                    {/* SELECT field */}
                                    {field.type === 'SELECT' && (
                                        <Select
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onValueChange={(value) => setCustomFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: value || null
                                            }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={`Select ${field.name.toLowerCase()}...`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {field.options.map((option) => (
                                                    <SelectItem key={option.id} value={option.id}>
                                                        <div className="flex items-center gap-2">
                                                            {option.color && (
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{ backgroundColor: option.color }}
                                                                />
                                                            )}
                                                            {option.label}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    {/* MULTI_SELECT field */}
                                    {field.type === 'MULTI_SELECT' && (
                                        <div className="space-y-2">
                                            <div className="border border-border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                                                {field.options.map((option) => {
                                                    const selected = Array.isArray(customFieldValues[field.id])
                                                        ? (customFieldValues[field.id] as string[]).includes(option.id)
                                                        : false;
                                                    return (
                                                        <label
                                                            key={option.id}
                                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-hover cursor-pointer"
                                                        >
                                                            <Checkbox
                                                                checked={selected}
                                                                onCheckedChange={(checked) => {
                                                                    const current = Array.isArray(customFieldValues[field.id])
                                                                        ? (customFieldValues[field.id] as string[])
                                                                        : [];
                                                                    if (checked) {
                                                                        setCustomFieldValues(prev => ({
                                                                            ...prev,
                                                                            [field.id]: [...current, option.id]
                                                                        }));
                                                                    } else {
                                                                        setCustomFieldValues(prev => ({
                                                                            ...prev,
                                                                            [field.id]: current.filter(id => id !== option.id)
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                            <div className="flex items-center gap-2">
                                                                {option.color && (
                                                                    <div
                                                                        className="w-3 h-3 rounded-full"
                                                                        style={{ backgroundColor: option.color }}
                                                                    />
                                                                )}
                                                                <span className="text-sm">{option.label}</span>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            {/* Selected values display */}
                                            {Array.isArray(customFieldValues[field.id]) && (customFieldValues[field.id] as string[]).length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {(customFieldValues[field.id] as string[]).map((optionId) => {
                                                        const option = field.options.find(o => o.id === optionId);
                                                        if (!option) return null;
                                                        return (
                                                            <Badge
                                                                key={optionId}
                                                                variant="secondary"
                                                                className="text-xs"
                                                                style={option.color ? {
                                                                    backgroundColor: `${option.color}20`,
                                                                    borderColor: option.color,
                                                                    color: option.color
                                                                } : undefined}
                                                            >
                                                                {option.label}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const current = customFieldValues[field.id] as string[];
                                                                        setCustomFieldValues(prev => ({
                                                                            ...prev,
                                                                            [field.id]: current.filter(id => id !== optionId)
                                                                        }));
                                                                    }}
                                                                    className="ml-1 hover:text-destructive"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* CHECKBOX field */}
                                    {field.type === 'CHECKBOX' && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id={`custom-${field.id}`}
                                                checked={(customFieldValues[field.id] as boolean) || false}
                                                onCheckedChange={(checked) => setCustomFieldValues(prev => ({
                                                    ...prev,
                                                    [field.id]: !!checked
                                                }))}
                                            />
                                            <Label htmlFor={`custom-${field.id}`} className="text-sm cursor-pointer">
                                                {field.name}
                                            </Label>
                                        </div>
                                    )}

                                    {/* URL field */}
                                    {field.type === 'URL' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            type="url"
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onChange={(e) => setCustomFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: e.target.value || null
                                            }))}
                                            placeholder="https://example.com"
                                        />
                                    )}

                                    {/* EMAIL field */}
                                    {field.type === 'EMAIL' && (
                                        <Input
                                            id={`custom-${field.id}`}
                                            type="email"
                                            value={(customFieldValues[field.id] as string) || ''}
                                            onChange={(e) => setCustomFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: e.target.value || null
                                            }))}
                                            placeholder="email@example.com"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {isEditing ? t('taskDialog.saveChanges') : t('taskDialog.createTask')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
